import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import { blogCategories, blogPosts } from '@/mocks/blogData';

export default function BlogListPage() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  const filteredPosts = activeCategory === 'all'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-firmai-dark" ref={sectionRef}>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-firmai-darker to-firmai-dark" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-firmai-green/8 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-firmai-blue/8 rounded-full blur-3xl" />

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">行业洞察</span>
              <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl text-white mt-4 leading-tight">
                能源与碳管理的
                <br />
                <span className="bg-gradient-to-r from-firmai-green to-firmai-blue bg-clip-text text-transparent">
                  专业见解
                </span>
              </h1>
              <p className="text-firmai-text mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
                来自 Firmai 专家团队的前沿洞察，涵盖行业趋势、技术突破、政策法规和最佳实践
              </p>
            </div>

            {/* Category Filter */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 flex flex-wrap items-center justify-center gap-2 mt-10">
              {blogCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-firmai-green text-firmai-dark'
                      : 'bg-firmai-card border border-firmai-border text-firmai-text hover:border-firmai-green/30 hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="relative py-8 md:py-12 bg-firmai-dark">
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              {/* Featured Post */}
              {activeCategory === 'all' && filteredPosts.length > 0 && (
                <div
                  className="reveal opacity-0 translate-y-6 transition-all duration-700 mb-10 cursor-pointer group"
                  onClick={() => navigate(`/blog/${filteredPosts[0].slug}`)}
                >
                  <div className="bg-firmai-card border border-firmai-border rounded-xl overflow-hidden hover:border-firmai-green/30 transition-all duration-300">
                    <div className="flex flex-col lg:flex-row">
                      <div className="w-full lg:w-[55%] h-64 lg:h-auto relative overflow-hidden">
                        <img
                          src={filteredPosts[0].image}
                          alt={filteredPosts[0].title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-firmai-card/60 hidden lg:block" />
                        <div className="absolute inset-0 bg-gradient-to-t from-firmai-card to-transparent lg:hidden" />
                      </div>
                      <div className="w-full lg:w-[45%] p-6 md:p-8 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="px-3 py-1 bg-firmai-green/20 text-firmai-green text-xs font-medium rounded-full border border-firmai-green/30">
                            {filteredPosts[0].categoryLabel}
                          </span>
                          <span className="text-firmai-muted text-xs">{filteredPosts[0].date}</span>
                          <span className="text-firmai-muted text-xs">{filteredPosts[0].readTime}阅读</span>
                        </div>
                        <h2 className="text-white font-semibold text-xl md:text-2xl leading-snug group-hover:text-firmai-green transition-colors duration-300">
                          {filteredPosts[0].title}
                        </h2>
                        <p className="text-firmai-text text-sm mt-3 leading-relaxed line-clamp-3">
                          {filteredPosts[0].excerpt}
                        </p>
                        <div className="flex items-center gap-3 mt-5">
                          <img
                            src={filteredPosts[0].author.avatar}
                            alt={filteredPosts[0].author.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div>
                            <div className="text-white text-sm">{filteredPosts[0].author.name}</div>
                            <div className="text-firmai-muted text-xs">{filteredPosts[0].author.title}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Post Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {(activeCategory === 'all' ? filteredPosts.slice(1) : filteredPosts).map((post, index) => (
                  <div
                    key={post.id}
                    className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl overflow-hidden hover:border-firmai-green/30 transition-all duration-300 cursor-pointer group"
                    style={{ transitionDelay: `${index * 80}ms` }}
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-firmai-card to-transparent" />
                      <span className="absolute top-3 left-3 px-3 py-1 bg-firmai-green/20 text-firmai-green text-xs font-medium rounded-full border border-firmai-green/30 backdrop-blur-sm">
                        {post.categoryLabel}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-firmai-muted text-xs">{post.date}</span>
                        <span className="text-firmai-muted text-xs">{post.readTime}</span>
                      </div>
                      <h3 className="text-white font-semibold text-base leading-snug group-hover:text-firmai-green transition-colors duration-300 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-firmai-text text-sm mt-2 leading-relaxed line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-firmai-border">
                        <img
                          src={post.author.avatar}
                          alt={post.author.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-firmai-text text-xs">{post.author.name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 bg-firmai-darker">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.06)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">订阅行业洞察</h2>
              <p className="text-firmai-text mt-4 text-base md:text-lg">
                每周获取最新的能源管理趋势、技术突破和合规动态
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="输入您的邮箱"
                  className="w-full px-4 py-3 bg-firmai-dark border border-firmai-border rounded-md text-white text-sm placeholder-firmai-muted focus:border-firmai-green focus:outline-none transition-colors"
                />
                <button className="w-full sm:w-auto px-6 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap">
                  立即订阅
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}